import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Plus, Edit, Trash2, Package, ShoppingCart, TrendingUp, ArrowLeft, Eye, Truck, CheckCircle, Clock, XCircle, Send, ExternalLink, Mail, Loader2, BarChart3, Users, MousePointer, Activity, Globe } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { categories as initialCategories } from '../mockData';
import { useToast } from '../hooks/use-toast';
import { productsAPI, ordersAPI, cloudinaryAPI } from '../services/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Admin = ({ cartCount }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [loginLoading, setLoginLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Load products from backend API
  const [products, setProducts] = useState([]);
  
  // Load orders from backend API
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [trackingInputs, setTrackingInputs] = useState({});
  const [sendingTracking, setSendingTracking] = useState({});
  
  const [categories, setCategories] = useState(initialCategories);
  const [editingProduct, setEditingProduct] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: '',
    scent: '',
    scentsWithImages: [], // Array of {scent: 'LAVENDER', images: [url1, url2, ...]}
    price: '',
    images: [], // Changed from 'image' to 'images' array
    description: '',
    category: 'Homemade Scented Candles', // Default category
    badges: [],
    inStock: true,
    stockCount: 0,
    weight: ''
  });

  // Category options
  const CATEGORY_OPTIONS = [
    'Homemade Scented Candles',
    'Homemade Resin Designs',
    'Perfect Gift'
  ];

  // Fetch analytics data
  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/analytics/stats`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  // Auto-refresh analytics every 30 seconds
  useEffect(() => {
    if (isAuthenticated) {
      fetchAnalytics();
      const interval = setInterval(fetchAnalytics, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  // Upload image to Cloudinary
  const handleCloudinaryUpload = async (file, scentIndex, isEditMode = false) => {
    setUploadingImage(true);
    try {
      const result = await cloudinaryAPI.uploadImage(file);
      
      if (isEditMode && editingProduct) {
        setEditingProduct(prev => ({
          ...prev,
          scentsWithImages: prev.scentsWithImages.map((s, i) =>
            i === scentIndex
              ? { ...s, images: [...s.images, result.url] }
              : s
          )
        }));
      } else {
        setNewProduct(prev => ({
          ...prev,
          scentsWithImages: prev.scentsWithImages.map((s, i) =>
            i === scentIndex
              ? { ...s, images: [...s.images, result.url] }
              : s
          )
        }));
      }
      
      toast({
        title: 'Image uploaded!',
        description: 'Image uploaded to cloud storage successfully.',
      });
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload failed',
        description: 'Failed to upload image. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setUploadingImage(false);
    }
  };

  // Load products from backend API on mount
  useEffect(() => {
    // Check if admin is already authenticated
    const checkAdminAuth = async () => {
      try {
        // Try with cookie first
        let response = await fetch(`${API_URL}/api/auth/admin/me`, {
          credentials: 'include'
        });
        
        // If cookie auth fails, try with stored token
        if (!response.ok) {
          const storedToken = localStorage.getItem('admin_session_token');
          if (storedToken) {
            response = await fetch(`${API_URL}/api/auth/admin/me`, {
              credentials: 'include',
              headers: {
                'Authorization': `Bearer ${storedToken}`
              }
            });
          }
        }
        
        if (response.ok) {
          setIsAuthenticated(true);
        } else {
          // Clear invalid stored token
          localStorage.removeItem('admin_session_token');
        }
      } catch (error) {
        console.log('Not authenticated');
        localStorage.removeItem('admin_session_token');
      }
    };
    checkAdminAuth();
    
    const loadProducts = async () => {
      try {
        const data = await productsAPI.getAll();
        setProducts(data);
      } catch (error) {
        console.error('Error loading products:', error);
        toast({
          title: 'Error',
          description: 'Failed to load products from server.',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };
    loadProducts();
  }, []);

  // Load orders when authenticated
  const loadOrders = async () => {
    setOrdersLoading(true);
    try {
      const data = await ordersAPI.getAll();
      setOrders(data);
    } catch (error) {
      console.error('Error loading orders:', error);
      toast({
        title: 'Error',
        description: 'Failed to load orders.',
        variant: 'destructive',
      });
    } finally {
      setOrdersLoading(false);
    }
  };

  // Update order status
  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    try {
      await ordersAPI.updateStatus(orderId, newStatus);
      setOrders(orders.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
      toast({
        title: 'Order Updated',
        description: `Order status changed to ${newStatus}`,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update order status.',
        variant: 'destructive',
      });
    }
  };

  // Add tracking number
  const handleAddTracking = async (orderId) => {
    const trackingNumber = trackingInputs[orderId];
    if (!trackingNumber || trackingNumber.trim() === '') {
      toast({
        title: 'Error',
        description: 'Please enter a tracking number',
        variant: 'destructive',
      });
      return;
    }

    setSendingTracking({ ...sendingTracking, [orderId]: true });
    try {
      const response = await ordersAPI.addTracking(orderId, trackingNumber);
      setOrders(orders.map(o => o.id === orderId ? { 
        ...o, 
        trackingNumber: response.trackingNumber,
        trackingUrl: response.trackingUrl,
        status: 'shipped'
      } : o));
      setTrackingInputs({ ...trackingInputs, [orderId]: '' });
      toast({
        title: 'Tracking Added! 📦',
        description: 'Customer has been notified via email',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to add tracking number.',
        variant: 'destructive',
      });
    } finally {
      setSendingTracking({ ...sendingTracking, [orderId]: false });
    }
  };

  // Delete order
  const handleDeleteOrder = async (orderId, orderNumber) => {
    if (!window.confirm(`Are you sure you want to delete order ${orderNumber}? This cannot be undone.`)) {
      return;
    }
    
    try {
      await ordersAPI.delete(orderId);
      setOrders(orders.filter(o => o.id !== orderId));
      toast({
        title: 'Order Deleted',
        description: `Order ${orderNumber} has been deleted.`,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete order.',
        variant: 'destructive',
      });
    }
  };

  // Get status badge color
  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock },
      paid: { bg: 'bg-blue-100', text: 'text-blue-700', icon: CheckCircle },
      processing: { bg: 'bg-purple-100', text: 'text-purple-700', icon: Package },
      shipped: { bg: 'bg-indigo-100', text: 'text-indigo-700', icon: Truck },
      delivered: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
      cancelled: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle },
    };
    return badges[status] || badges.pending;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    // Prevent multiple submissions
    if (loginLoading) {
      return;
    }
    
    // Validate inputs before making request
    if (!email || !password) {
      toast({
        title: 'Missing credentials',
        description: 'Please enter both email and password.',
        variant: 'destructive',
      });
      return;
    }
    
    setLoginLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: email.trim(), password })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }
      
      // Store session token in localStorage as backup
      if (data.session_token) {
        localStorage.setItem('admin_session_token', data.session_token);
      }
      
      setIsAuthenticated(true);
      toast({
        title: 'Login successful!',
        description: `Welcome, ${data.name}!`,
      });
    } catch (error) {
      toast({
        title: 'Login failed',
        description: error.message || 'Invalid credentials. Please try again.',
        variant: 'destructive',
      });
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/admin/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    // Clear stored token
    localStorage.removeItem('admin_session_token');
    setIsAuthenticated(false);
    toast({
      title: 'Logged out',
      description: 'You have been logged out of the admin panel.',
    });
  };

  const handleAddProduct = async () => {
    if (newProduct.scentsWithImages.length === 0) {
      toast({
        title: 'Error',
        description: 'Please add at least one scent with images',
        variant: 'destructive',
      });
      return;
    }
    
    // Check if all scents have images
    const scentsWithoutImages = newProduct.scentsWithImages.filter(s => s.images.length === 0);
    if (scentsWithoutImages.length > 0) {
      toast({
        title: 'Error',
        description: `Please add images for: ${scentsWithoutImages.map(s => s.scent).join(', ')}`,
        variant: 'destructive',
      });
      return;
    }
    
    try {
      const productData = {
        name: newProduct.name,
        price: parseFloat(newProduct.price),
        description: newProduct.description,
        category: newProduct.category,
        badges: newProduct.badges,
        inStock: newProduct.inStock,
        stockCount: parseInt(newProduct.stockCount) || 0,
        weight: newProduct.weight,
        scent: newProduct.scentsWithImages[0]?.scent || '',
        scentsWithImages: newProduct.scentsWithImages
      };
      
      const createdProduct = await productsAPI.create(productData);
      setProducts([...products, createdProduct]);
      
      toast({
        title: 'Product added!',
        description: `${createdProduct.name} has been added with ${createdProduct.scentsWithImages.length} scent options.`,
      });
      
      setNewProduct({
        name: '',
        scent: '',
        scentsWithImages: [],
        price: '',
        images: [],
        description: '',
        category: 'Homemade Scented Candles',
        badges: [],
        inStock: true,
        stockCount: 0,
        weight: ''
      });
      setIsDialogOpen(false);
    } catch (error) {
      console.error('Error adding product:', error);
      toast({
        title: 'Error',
        description: 'Failed to add product. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleUpdateProduct = async () => {
    try {
      const updatedProduct = await productsAPI.update(editingProduct.id, editingProduct);
      setProducts(products.map(p => p.id === editingProduct.id ? updatedProduct : p));
      toast({
        title: 'Product updated!',
        description: `${updatedProduct.name} has been updated.`,
      });
      setEditingProduct(null);
      setIsDialogOpen(false);
    } catch (error) {
      console.error('Error updating product:', error);
      toast({
        title: 'Error',
        description: 'Failed to update product. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteProduct = async (id) => {
    try {
      await productsAPI.delete(id);
      setProducts(products.filter(p => p.id !== id));
      toast({
        title: 'Product deleted!',
        description: 'The product has been removed from the catalog.',
      });
    } catch (error) {
      console.error('Error deleting product:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete product. Please try again.',
        variant: 'destructive',
      });
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-black">
        <Navbar cartCount={cartCount} />
        <div className="container mx-auto px-6 py-20">
          <div className="max-w-md mx-auto bg-white rounded-2xl shadow-xl p-8">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-amber-400 rounded-full flex items-center justify-center">
                <Lock className="w-8 h-8 text-[#8B4545]" />
              </div>
            </div>
            <h2 className="text-3xl font-serif font-bold text-[#8B4545] text-center mb-2">
              Admin Login
            </h2>
            <p className="text-gray-600 text-center mb-8">
              Enter your credentials to access the admin panel
            </p>
            <form onSubmit={handleLogin} className="space-y-6" data-testid="admin-login-form">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@example.com"
                    required
                    className="pl-10 bg-amber-50 border-[#8B4545]/20"
                    data-testid="admin-email-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter admin password"
                    required
                    className="pl-10 bg-amber-50 border-[#8B4545]/20"
                    data-testid="admin-password-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loginLoading || !email || !password}
                className="w-full bg-amber-400 hover:bg-amber-500 text-[#8B4545] font-semibold py-6 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="admin-login-button"
              >
                {loginLoading ? (
                  <span className="flex items-center justify-center gap-2" data-testid="admin-login-loading">
                    <Loader2 className="w-5 h-5 animate-spin" data-testid="admin-login-spinner" />
                    Signing in...
                  </span>
                ) : 'Login'}
              </Button>
            </form>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <Navbar cartCount={cartCount} />

      <div className="bg-gradient-to-br from-[#8B4545] via-[#9B5555] to-[#7B3535] py-12 text-white">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Home</span>
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-colors"
            >
              <span>Logout</span>
            </button>
          </div>
          <h1 className="text-4xl md:text-5xl font-serif font-bold mb-4">Admin Dashboard</h1>
          <p className="text-amber-100">Manage your products, inventory, and orders</p>
        </div>
      </div>

      <div className="container mx-auto px-6 py-12">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm mb-1">Total Products</p>
                <p className="text-3xl font-bold text-[#8B4545]">{products.length}</p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <Package className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm mb-1">Total Orders</p>
                <p className="text-3xl font-bold text-[#8B4545]">{orders.length}</p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <ShoppingCart className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm mb-1">Revenue</p>
                <p className="text-3xl font-bold text-[#8B4545]">
                  £{orders.reduce((sum, o) => sum + (o.total || 0), 0).toFixed(2)}
                </p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="products" className="space-y-6" onValueChange={(value) => {
          if (value === 'orders' && orders.length === 0) {
            loadOrders();
          }
        }}>
          <TabsList className="bg-white shadow-md">
            <TabsTrigger value="products">Products</TabsTrigger>
            <TabsTrigger value="categories">Categories</TabsTrigger>
            <TabsTrigger value="orders">Orders ({orders.length})</TabsTrigger>
            <TabsTrigger value="analytics" className="flex items-center gap-1">
              <BarChart3 className="w-4 h-4" />
              Analytics
            </TabsTrigger>
          </TabsList>

          {/* Products Tab */}
          <TabsContent value="products">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-serif font-bold text-[#8B4545]">Products</h2>
                <Dialog open={isDialogOpen && !editingProduct} onOpenChange={setIsDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-amber-400 hover:bg-amber-500 text-[#8B4545] font-semibold">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Product
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#5C1414] border-[#A52A2A]">
                    <DialogHeader>
                      <DialogTitle className="text-2xl font-serif text-amber-100">Add New Product</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <label className="block text-sm font-medium mb-2 text-amber-200">Product Name</label>
                        <Input
                          value={newProduct.name}
                          onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                          placeholder="Enter product name"
                          className="bg-[#6B1515] border-[#A52A2A] text-white placeholder-white/50"
                        />
                      </div>
                      
                      {/* Category Selection */}
                      <div>
                        <label className="block text-sm font-medium mb-2 text-amber-200">Category</label>
                        <Select 
                          value={newProduct.category} 
                          onValueChange={(value) => setNewProduct({...newProduct, category: value})}
                        >
                          <SelectTrigger className="bg-[#6B1515] border-[#A52A2A] text-white">
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent>
                            {CATEGORY_OPTIONS.map(cat => (
                              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Product-Scent Management Card */}
                      {newProduct.name && (
                        <div className="bg-amber-400/10 border-2 border-amber-400 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                            </svg>
                            <p className="text-sm font-bold text-amber-400">
                              Scents for: <span className="text-white">{newProduct.name}</span>
                            </p>
                          </div>
                          <p className="text-xs text-amber-200/70">
                            Add scent options that customers can choose for this product
                          </p>
                        </div>
                      )}
                      {/* Scent-Image Management Section */}
                      <div className="border-2 border-amber-400 rounded-lg p-6 bg-[#8B4545]/20">
                        <div className="flex items-center gap-2 mb-4">
                          <svg className="w-6 h-6 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <h3 className="text-lg font-bold text-amber-400">
                            Scent-Specific Images for {newProduct.name || 'Product'}
                          </h3>
                        </div>
                        <p className="text-sm text-amber-200/70 mb-4">
                          Add scents and assign specific images to each scent option
                        </p>

                        {/* Add New Scent with Images */}
                        <div className="bg-[#6B1515] rounded-lg p-4 mb-4">
                          <label className="block text-sm font-semibold text-amber-200 mb-2">
                            Step 1: Add Scent Name
                          </label>
                          <div className="flex gap-2 mb-2">
                            <Input
                              id="newScentName"
                              placeholder="Enter scent name (e.g., LAVENDER)"
                              className="bg-[#5C1414] border-[#A52A2A] text-white placeholder-white/50 flex-1"
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  const scent = e.target.value.trim().toUpperCase();
                                  if (scent && !newProduct.scentsWithImages.find(s => s.scent === scent)) {
                                    setNewProduct({
                                      ...newProduct,
                                      scentsWithImages: [...newProduct.scentsWithImages, { scent, images: [] }]
                                    });
                                    e.target.value = '';
                                  }
                                }
                              }}
                            />
                            <Button
                              type="button"
                              onClick={() => {
                                const input = document.getElementById('newScentName');
                                const scent = input.value.trim().toUpperCase();
                                if (scent && !newProduct.scentsWithImages.find(s => s.scent === scent)) {
                                  setNewProduct({
                                    ...newProduct,
                                    scentsWithImages: [...newProduct.scentsWithImages, { scent, images: [] }]
                                  });
                                  input.value = '';
                                } else if (!scent) {
                                  toast({
                                    title: 'Enter a scent name',
                                    description: 'Please type a scent name first',
                                    variant: 'destructive',
                                  });
                                }
                              }}
                              className="bg-amber-400 hover:bg-amber-500 text-black font-semibold px-6"
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Add Scent
                            </Button>
                          </div>
                          <p className="text-xs text-amber-200/60">
                            Add a scent name first, then you can upload images for it below
                          </p>
                        </div>

                        {/* Display Scents with Image Management */}
                        {newProduct.scentsWithImages.length > 0 && (
                          <div className="space-y-4">
                            {newProduct.scentsWithImages.map((scentObj, scentIndex) => (
                              <div key={scentIndex} className="bg-[#6B1515] rounded-lg p-4 border border-amber-400/30">
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-2">
                                    <span className="bg-amber-400 text-black px-3 py-1 rounded-full text-sm font-bold">
                                      {scentObj.scent}
                                    </span>
                                    <span className="text-xs text-amber-200">
                                      {scentObj.images.length} / 5 images
                                    </span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setNewProduct({
                                        ...newProduct,
                                        scentsWithImages: newProduct.scentsWithImages.filter((_, i) => i !== scentIndex)
                                      });
                                    }}
                                    className="text-red-400 hover:text-red-600 font-bold"
                                  >
                                    Remove Scent
                                  </button>
                                </div>

                                {/* Image Upload for this Scent - Using Cloudinary */}
                                <div className="border-2 border-dashed border-amber-400/50 rounded-lg p-4 bg-[#5C1414]/50">
                                  <p className="text-sm font-semibold text-amber-200 mb-3">Step 2: Upload Images for {scentObj.scent}</p>
                                  <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    disabled={uploadingImage}
                                    onChange={(e) => {
                                      const files = Array.from(e.target.files || []);
                                      if (files.length + scentObj.images.length > 5) {
                                        toast({
                                          title: 'Too many images',
                                          description: 'Maximum 5 images per scent',
                                          variant: 'destructive',
                                        });
                                        return;
                                      }
                                      // Upload each file to Cloudinary
                                      files.forEach(file => {
                                        handleCloudinaryUpload(file, scentIndex, false);
                                      });
                                    }}
                                    className="hidden"
                                    id={`scent-images-${scentIndex}`}
                                  />
                                  <label
                                    htmlFor={`scent-images-${scentIndex}`}
                                    className={`cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-amber-400 hover:bg-amber-500 text-black font-semibold rounded-lg transition-colors ${uploadingImage ? 'opacity-50 cursor-not-allowed' : ''}`}
                                  >
                                    {uploadingImage ? (
                                      <>
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        Uploading...
                                      </>
                                    ) : (
                                      <>
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                        </svg>
                                        Click to Upload Images
                                      </>
                                    )}
                                  </label>
                                  <p className="text-xs text-amber-200/60 mt-2">
                                    Click the button above to select images from your device
                                  </p>

                                  {/* Display Images for this Scent */}
                                  {scentObj.images.length > 0 && (
                                    <div className="grid grid-cols-5 gap-2 mt-3">
                                      {scentObj.images.map((img, imgIndex) => (
                                        <div key={imgIndex} className="relative group">
                                          <img src={img} alt={`${scentObj.scent} ${imgIndex + 1}`} className="w-full h-20 object-cover rounded border border-amber-400" />
                                          <button
                                            type="button"
                                            onClick={() => {
                                              setNewProduct({
                                                ...newProduct,
                                                scentsWithImages: newProduct.scentsWithImages.map((s, i) =>
                                                  i === scentIndex
                                                    ? { ...s, images: s.images.filter((_, j) => j !== imgIndex) }
                                                    : s
                                                )
                                              });
                                            }}
                                            className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-red-600"
                                          >
                                            ×
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {/* URL Input for this Scent */}
                                  <div className="mt-3">
                                    <textarea
                                      placeholder={`Image URLs for ${scentObj.scent} (one per line)`}
                                      rows={2}
                                      className="w-full bg-[#5C1414] border-[#A52A2A] text-white placeholder-white/50 rounded p-2 text-xs"
                                      onChange={(e) => {
                                        const urls = e.target.value.split('\n').filter(url => url.trim());
                                        if (urls.length > 5) {
                                          alert('Maximum 5 images per scent');
                                          return;
                                        }
                                        setNewProduct({
                                          ...newProduct,
                                          scentsWithImages: newProduct.scentsWithImages.map((s, i) =>
                                            i === scentIndex ? { ...s, images: urls } : s
                                          )
                                        });
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {newProduct.scentsWithImages.length === 0 && (
                          <div className="text-center py-6 text-amber-200/60">
                            <p className="text-sm">No scent options added yet</p>
                            <p className="text-xs mt-1">Add a scent above to start uploading images</p>
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2 text-amber-200">Description</label>
                        <Textarea
                          value={newProduct.description}
                          onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                          placeholder="Product description..."
                          rows={3}
                          className="bg-[#6B1515] border-[#A52A2A] text-white placeholder-white/50"
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2 text-amber-200">Price (£)</label>
                          <Input
                            type="number"
                            step="0.01"
                            value={newProduct.price}
                            onChange={(e) => setNewProduct({...newProduct, price: e.target.value})}
                            placeholder="12.90"
                            className="bg-[#6B1515] border-[#A52A2A] text-white placeholder-white/50"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2 text-amber-200">Weight</label>
                          <Input
                            type="text"
                            value={newProduct.weight}
                            onChange={(e) => setNewProduct({...newProduct, weight: e.target.value})}
                            placeholder="200g"
                            className="bg-[#6B1515] border-[#A52A2A] text-white placeholder-white/50"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2 text-amber-200">Quantity</label>
                          <Input
                            type="number"
                            value={newProduct.stockCount}
                            onChange={(e) => setNewProduct({...newProduct, stockCount: e.target.value})}
                            placeholder="10"
                            className="bg-[#6B1515] border-[#A52A2A] text-white placeholder-white/50"
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-3 pt-2">
                        <div className="flex items-center justify-between bg-[#6B1515]/50 p-3 rounded-lg">
                          <label className="text-sm font-medium text-amber-200">In Stock</label>
                          <button
                            type="button"
                            onClick={() => setNewProduct({...newProduct, inStock: !newProduct.inStock})}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${newProduct.inStock ? 'bg-amber-400' : 'bg-gray-600'}`}
                          >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${newProduct.inStock ? 'translate-x-6' : 'translate-x-1'}`} />
                          </button>
                        </div>
                        <div className="flex items-center justify-between bg-[#6B1515]/50 p-3 rounded-lg">
                          <label className="text-sm font-medium text-amber-200">New Arrival</label>
                          <button
                            type="button"
                            onClick={() => {
                              const hasNew = newProduct.badges.includes('New');
                              setNewProduct({
                                ...newProduct, 
                                badges: hasNew 
                                  ? newProduct.badges.filter(b => b !== 'New')
                                  : [...newProduct.badges, 'New']
                              });
                            }}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${newProduct.badges.includes('New') ? 'bg-amber-400' : 'bg-gray-600'}`}
                          >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${newProduct.badges.includes('New') ? 'translate-x-6' : 'translate-x-1'}`} />
                          </button>
                        </div>
                        <div className="flex items-center justify-between bg-[#6B1515]/50 p-3 rounded-lg">
                          <label className="text-sm font-medium text-amber-200">Bestseller</label>
                          <button
                            type="button"
                            onClick={() => {
                              const hasBestseller = newProduct.badges.includes('Bestseller');
                              setNewProduct({
                                ...newProduct, 
                                badges: hasBestseller 
                                  ? newProduct.badges.filter(b => b !== 'Bestseller')
                                  : [...newProduct.badges, 'Bestseller']
                              });
                            }}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${newProduct.badges.includes('Bestseller') ? 'bg-amber-400' : 'bg-gray-600'}`}
                          >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${newProduct.badges.includes('Bestseller') ? 'translate-x-6' : 'translate-x-1'}`} />
                          </button>
                        </div>
                      </div>
                      <div className="flex gap-3 pt-4">
                        <Button
                          onClick={handleAddProduct}
                          className="flex-1 bg-amber-400 hover:bg-amber-500 text-[#6B1515] font-semibold py-6 text-base"
                        >
                          Save Product
                        </Button>
                        <Button
                          onClick={() => {
                            setIsDialogOpen(false);
                            setNewProduct({
                              name: '',
                              scent: '',
                              price: '',
                              image: '',
                              description: '',
                              badges: [],
                              inStock: true,
                              stockCount: 0,
                              weight: ''
                            });
                          }}
                          variant="outline"
                          className="flex-1 border-amber-400 text-amber-400 hover:bg-amber-400/10 py-6 text-base"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-black">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Image</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Name</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Category</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Price</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Stock</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Badges</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {products.map(product => {
                      // Get the first image from scentsWithImages
                      const productImage = product.scentsWithImages?.[0]?.images?.[0] || product.image || 'https://via.placeholder.com/100';
                      return (
                      <tr key={product.id} className="hover:bg-black">
                        <td className="px-4 py-3">
                          <img src={productImage} alt={product.name} className="w-16 h-16 object-cover rounded" />
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-semibold text-gray-800">{product.name}</p>
                          <p className="text-sm text-gray-600">{product.scent}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 rounded text-xs font-semibold bg-amber-100 text-amber-700">
                            {product.category || 'Uncategorized'}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-semibold text-[#8B4545]">£{product.price?.toFixed(2) || '0.00'}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${product.inStock ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {product.inStock ? `${product.stockCount || 0} in stock` : 'Out of stock'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1">
                            {(product.badges || []).map(badge => (
                              <span key={badge} className="px-2 py-1 rounded text-xs font-semibold bg-amber-100 text-amber-700">
                                {badge}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex gap-2 justify-end">
                            <Dialog open={isDialogOpen && editingProduct?.id === product.id} onOpenChange={(open) => {
                              setIsDialogOpen(open);
                              if (!open) setEditingProduct(null);
                            }}>
                              <DialogTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setEditingProduct({...product, scentsWithImages: product.scentsWithImages || []})}
                                  className="border-[#8B4545] text-[#8B4545] hover:bg-[#8B4545] hover:text-white"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#5C1414] border-[#A52A2A]">
                                <DialogHeader>
                                  <DialogTitle className="text-2xl font-serif text-amber-100">Edit Product</DialogTitle>
                                </DialogHeader>
                                {editingProduct && (
                                  <div className="space-y-4 mt-4">
                                    {/* Product Name */}
                                    <div>
                                      <label className="block text-sm font-medium mb-2 text-amber-200">Product Name</label>
                                      <Input
                                        value={editingProduct.name || ''}
                                        onChange={(e) => setEditingProduct({...editingProduct, name: e.target.value})}
                                        className="bg-[#6B1515] border-[#A52A2A] text-white"
                                      />
                                    </div>
                                    
                                    {/* Category Selection */}
                                    <div>
                                      <label className="block text-sm font-medium mb-2 text-amber-200">Category</label>
                                      <Select 
                                        value={editingProduct.category || 'Homemade Scented Candles'} 
                                        onValueChange={(value) => setEditingProduct({...editingProduct, category: value})}
                                      >
                                        <SelectTrigger className="bg-[#6B1515] border-[#A52A2A] text-white">
                                          <SelectValue placeholder="Select category" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {CATEGORY_OPTIONS.map(cat => (
                                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    
                                    {/* Scents with Images Section */}
                                    <div className="bg-amber-400/10 border-2 border-amber-400 rounded-lg p-4">
                                      <div className="flex items-center gap-2 mb-3">
                                        <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                        </svg>
                                        <p className="text-sm font-bold text-amber-400">
                                          Scent Options with Images
                                        </p>
                                      </div>
                                      
                                      {/* Add New Scent */}
                                      <div className="flex gap-2 mb-3">
                                        <Input
                                          id={`edit-scent-input-${product.id}`}
                                          placeholder="Enter scent name (e.g., LAVENDER)"
                                          className="bg-[#6B1515] border-[#A52A2A] text-white placeholder-white/50 flex-1"
                                          onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                              e.preventDefault();
                                              const scent = e.target.value.trim().toUpperCase();
                                              if (scent && !editingProduct.scentsWithImages?.find(s => s.scent === scent)) {
                                                setEditingProduct({
                                                  ...editingProduct,
                                                  scentsWithImages: [...(editingProduct.scentsWithImages || []), { scent, images: [], isDefault: false }]
                                                });
                                                e.target.value = '';
                                              }
                                            }
                                          }}
                                        />
                                        <Button
                                          type="button"
                                          onClick={() => {
                                            const input = document.getElementById(`edit-scent-input-${product.id}`);
                                            const scent = input.value.trim().toUpperCase();
                                            if (scent && !editingProduct.scentsWithImages?.find(s => s.scent === scent)) {
                                              setEditingProduct({
                                                ...editingProduct,
                                                scentsWithImages: [...(editingProduct.scentsWithImages || []), { scent, images: [], isDefault: false }]
                                              });
                                              input.value = '';
                                            }
                                          }}
                                          className="bg-amber-400 hover:bg-amber-500 text-black"
                                        >
                                          Add Scent
                                        </Button>
                                      </div>
                                      
                                      {/* Display Scents */}
                                      {editingProduct.scentsWithImages && editingProduct.scentsWithImages.length > 0 ? (
                                        <div className="space-y-3">
                                          {editingProduct.scentsWithImages.map((scentObj, index) => (
                                            <div key={index} className="bg-[#6B1515] rounded-lg p-3">
                                              <div className="flex items-center justify-between mb-2">
                                                <span className="font-semibold text-amber-400">{scentObj.scent}</span>
                                                <div className="flex gap-2">
                                                  <button
                                                    type="button"
                                                    onClick={() => {
                                                      const updated = editingProduct.scentsWithImages.map((s, i) => ({
                                                        ...s,
                                                        isDefault: i === index
                                                      }));
                                                      setEditingProduct({...editingProduct, scentsWithImages: updated, scent: scentObj.scent});
                                                    }}
                                                    className={`text-xs px-2 py-1 rounded ${scentObj.isDefault ? 'bg-green-500 text-white' : 'bg-gray-600 text-white hover:bg-green-500'}`}
                                                  >
                                                    {scentObj.isDefault ? '✓ Default' : 'Set Default'}
                                                  </button>
                                                  <button
                                                    type="button"
                                                    onClick={() => {
                                                      setEditingProduct({
                                                        ...editingProduct,
                                                        scentsWithImages: editingProduct.scentsWithImages.filter((_, i) => i !== index)
                                                      });
                                                    }}
                                                    className="text-red-400 hover:text-red-300 text-sm"
                                                  >
                                                    ✕ Remove
                                                  </button>
                                                </div>
                                              </div>
                                              
                                              {/* Images for this scent */}
                                              <div className="flex flex-wrap gap-2 mb-2">
                                                {scentObj.images && scentObj.images.map((img, imgIndex) => (
                                                  <div key={imgIndex} className="relative">
                                                    <img src={img} alt="" className="w-16 h-16 object-cover rounded" />
                                                    <button
                                                      type="button"
                                                      onClick={() => {
                                                        const updated = [...editingProduct.scentsWithImages];
                                                        updated[index].images = updated[index].images.filter((_, i) => i !== imgIndex);
                                                        setEditingProduct({...editingProduct, scentsWithImages: updated});
                                                      }}
                                                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 text-xs"
                                                    >
                                                      ✕
                                                    </button>
                                                  </div>
                                                ))}
                                              </div>
                                              
                                              {/* Add image to scent */}
                                              <div className="flex gap-2">
                                                <Input
                                                  type="text"
                                                  placeholder="Image URL"
                                                  id={`edit-img-${product.id}-${index}`}
                                                  className="bg-[#5C1414] border-[#A52A2A] text-white placeholder-white/50 text-sm flex-1"
                                                />
                                                <Button
                                                  type="button"
                                                  size="sm"
                                                  onClick={() => {
                                                    const input = document.getElementById(`edit-img-${product.id}-${index}`);
                                                    const url = input.value.trim();
                                                    if (url) {
                                                      const updated = [...editingProduct.scentsWithImages];
                                                      updated[index].images = [...(updated[index].images || []), url];
                                                      setEditingProduct({...editingProduct, scentsWithImages: updated});
                                                      input.value = '';
                                                    }
                                                  }}
                                                  className="bg-amber-400 hover:bg-amber-500 text-black text-xs"
                                                >
                                                  Add Image
                                                </Button>
                                                <label className="cursor-pointer bg-gray-600 hover:bg-gray-500 text-white px-2 py-1 rounded text-xs flex items-center">
                                                  Upload
                                                  <input
                                                    type="file"
                                                    accept="image/*"
                                                    className="hidden"
                                                    disabled={uploadingImage}
                                                    onChange={(e) => {
                                                      const file = e.target.files?.[0];
                                                      if (file) {
                                                        handleCloudinaryUpload(file, index, true);
                                                      }
                                                    }}
                                                  />
                                                </label>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <p className="text-amber-200/60 text-sm">No scents added yet. Add scents above.</p>
                                      )}
                                    </div>
                                    
                                    {/* Description */}
                                    <div>
                                      <label className="block text-sm font-medium mb-2 text-amber-200">Description</label>
                                      <Textarea
                                        value={editingProduct.description || ''}
                                        onChange={(e) => setEditingProduct({...editingProduct, description: e.target.value})}
                                        rows={3}
                                        className="bg-[#6B1515] border-[#A52A2A] text-white"
                                      />
                                    </div>
                                    
                                    {/* Price, Weight, Quantity */}
                                    <div className="grid grid-cols-3 gap-4">
                                      <div>
                                        <label className="block text-sm font-medium mb-2 text-amber-200">Price (£)</label>
                                        <Input
                                          type="number"
                                          step="0.01"
                                          value={editingProduct.price || ''}
                                          onChange={(e) => setEditingProduct({...editingProduct, price: parseFloat(e.target.value) || 0})}
                                          className="bg-[#6B1515] border-[#A52A2A] text-white"
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-sm font-medium mb-2 text-amber-200">Weight</label>
                                        <Input
                                          type="text"
                                          value={editingProduct.weight || ''}
                                          onChange={(e) => setEditingProduct({...editingProduct, weight: e.target.value})}
                                          className="bg-[#6B1515] border-[#A52A2A] text-white"
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-sm font-medium mb-2 text-amber-200">Quantity</label>
                                        <Input
                                          type="number"
                                          value={editingProduct.stockCount || 0}
                                          onChange={(e) => setEditingProduct({...editingProduct, stockCount: parseInt(e.target.value) || 0})}
                                          className="bg-[#6B1515] border-[#A52A2A] text-white"
                                        />
                                      </div>
                                    </div>
                                    
                                    {/* Toggles */}
                                    <div className="space-y-3 pt-2">
                                      <div className="flex items-center justify-between bg-[#6B1515]/50 p-3 rounded-lg">
                                        <label className="text-sm font-medium text-amber-200">In Stock</label>
                                        <button
                                          type="button"
                                          onClick={() => setEditingProduct({...editingProduct, inStock: !editingProduct.inStock})}
                                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${editingProduct.inStock ? 'bg-amber-400' : 'bg-gray-600'}`}
                                        >
                                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${editingProduct.inStock ? 'translate-x-6' : 'translate-x-1'}`} />
                                        </button>
                                      </div>
                                      <div className="flex items-center justify-between bg-[#6B1515]/50 p-3 rounded-lg">
                                        <label className="text-sm font-medium text-amber-200">New Arrival</label>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const badges = editingProduct.badges || [];
                                            const hasNew = badges.includes('New');
                                            setEditingProduct({
                                              ...editingProduct, 
                                              badges: hasNew 
                                                ? badges.filter(b => b !== 'New')
                                                : [...badges, 'New']
                                            });
                                          }}
                                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${(editingProduct.badges || []).includes('New') ? 'bg-amber-400' : 'bg-gray-600'}`}
                                        >
                                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${(editingProduct.badges || []).includes('New') ? 'translate-x-6' : 'translate-x-1'}`} />
                                        </button>
                                      </div>
                                      <div className="flex items-center justify-between bg-[#6B1515]/50 p-3 rounded-lg">
                                        <label className="text-sm font-medium text-amber-200">Bestseller</label>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const badges = editingProduct.badges || [];
                                            const hasBestseller = badges.includes('Bestseller');
                                            setEditingProduct({
                                              ...editingProduct, 
                                              badges: hasBestseller 
                                                ? badges.filter(b => b !== 'Bestseller')
                                                : [...badges, 'Bestseller']
                                            });
                                          }}
                                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${(editingProduct.badges || []).includes('Bestseller') ? 'bg-amber-400' : 'bg-gray-600'}`}
                                        >
                                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${(editingProduct.badges || []).includes('Bestseller') ? 'translate-x-6' : 'translate-x-1'}`} />
                                        </button>
                                      </div>
                                    </div>
                                    
                                    {/* Buttons */}
                                    <div className="flex gap-3 pt-4">
                                      <Button
                                        onClick={handleUpdateProduct}
                                        className="flex-1 bg-amber-400 hover:bg-amber-500 text-[#6B1515] font-semibold py-6 text-base"
                                      >
                                        Update Product
                                      </Button>
                                      <Button
                                        onClick={() => {
                                          setIsDialogOpen(false);
                                          setEditingProduct(null);
                                        }}
                                        variant="outline"
                                        className="flex-1 border-amber-400 text-amber-400 hover:bg-amber-400/10 py-6 text-base"
                                      >
                                        Cancel
                                      </Button>
                                    </div>
                                  </div>
                                )}
                              </DialogContent>
                            </Dialog>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteProduct(product.id)}
                              className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Categories Tab */}
          <TabsContent value="categories">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-2xl font-serif font-bold text-[#8B4545] mb-6">Categories</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categories.map(category => (
                  <div key={category} className="bg-amber-50 rounded-lg p-4 flex items-center justify-center">
                    <span className="font-semibold text-[#8B4545]">{category}</span>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-serif font-bold text-[#8B4545]">Orders</h2>
                <Button onClick={loadOrders} variant="outline" className="text-[#8B4545] border-[#8B4545]">
                  Refresh Orders
                </Button>
              </div>
              
              {ordersLoading ? (
                <div className="text-center py-12">
                  <div className="animate-spin w-8 h-8 border-4 border-[#8B4545] border-t-transparent rounded-full mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading orders...</p>
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-12">
                  <ShoppingCart className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-600 mb-4">No orders yet</p>
                  <Button onClick={loadOrders} className="bg-[#8B4545] hover:bg-[#7B3535] text-white">
                    Load Orders
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {orders.map(order => {
                    const statusBadge = getStatusBadge(order.status);
                    const StatusIcon = statusBadge.icon;
                    return (
                      <div key={order.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <p className="font-semibold text-lg">{order.orderNumber}</p>
                            <p className="text-sm text-gray-600">
                              {new Date(order.createdAt).toLocaleDateString('en-GB', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${statusBadge.bg} ${statusBadge.text}`}>
                            <StatusIcon className="w-3 h-3" />
                            {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                          <div>
                            <p className="text-xs text-gray-500 uppercase">Customer</p>
                            <p className="font-medium">{order.customer?.firstName} {order.customer?.lastName}</p>
                            <p className="text-sm text-gray-600">{order.customer?.email}</p>
                            <p className="text-sm text-gray-600">{order.customer?.phone}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase">Shipping Address</p>
                            <p className="text-sm text-gray-700">{order.customer?.address}</p>
                            <p className="text-sm text-gray-700">{order.customer?.city}, {order.customer?.postcode}</p>
                            <p className="text-sm text-gray-600">{order.shipping?.methodName}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase">Total</p>
                            <p className="font-bold text-xl text-[#8B4545]">£{order.total?.toFixed(2)}</p>
                            <p className="text-xs text-gray-500">{order.items?.length} item(s)</p>
                          </div>
                        </div>
                        
                        {/* Order Items */}
                        <div className="bg-gray-50 rounded-lg p-3 mb-4">
                          <p className="text-xs text-gray-500 uppercase mb-2">Items</p>
                          <div className="space-y-2">
                            {order.items?.map((item, idx) => (
                              <div key={idx} className="flex justify-between text-sm">
                                <span>{item.name} {item.scent && `(${item.scent})`} x{item.quantity}</span>
                                <span className="font-medium">£{(item.price * item.quantity).toFixed(2)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        {/* Tracking Number Section */}
                        <div className="bg-indigo-50 rounded-lg p-4 mb-4">
                          <div className="flex items-center gap-2 mb-3">
                            <Truck className="w-5 h-5 text-indigo-600" />
                            <span className="font-semibold text-indigo-900">Shipping & Tracking</span>
                          </div>
                          
                          {order.trackingNumber ? (
                            <div className="space-y-2">
                              <div className="flex items-center justify-between bg-white rounded p-3">
                                <div>
                                  <p className="text-xs text-gray-500">Tracking Number</p>
                                  <p className="font-mono font-bold text-lg">{order.trackingNumber}</p>
                                </div>
                                <a 
                                  href={order.trackingUrl || `https://www.royalmail.com/track-your-item#/tracking-results/${order.trackingNumber}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-1 text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                                >
                                  Track <ExternalLink className="w-4 h-4" />
                                </a>
                              </div>
                              <p className="text-xs text-green-600">✓ Customer has been notified</p>
                            </div>
                          ) : (
                            <div className="flex gap-2">
                              <Input
                                placeholder="Enter Royal Mail tracking number"
                                value={trackingInputs[order.id] || ''}
                                onChange={(e) => setTrackingInputs({ ...trackingInputs, [order.id]: e.target.value })}
                                className="flex-1 font-mono uppercase"
                              />
                              <Button
                                onClick={() => handleAddTracking(order.id)}
                                disabled={sendingTracking[order.id]}
                                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                              >
                                {sendingTracking[order.id] ? (
                                  <span className="animate-spin">⏳</span>
                                ) : (
                                  <>
                                    <Send className="w-4 h-4 mr-1" />
                                    Add & Notify
                                  </>
                                )}
                              </Button>
                            </div>
                          )}
                        </div>

                        {/* Status Update & Delete */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">Update Status:</span>
                            <Select
                              value={order.status}
                              onValueChange={(value) => handleUpdateOrderStatus(order.id, value)}
                            >
                              <SelectTrigger className="w-40">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="paid">Paid</SelectItem>
                              <SelectItem value="processing">Processing</SelectItem>
                              <SelectItem value="shipped">Shipped</SelectItem>
                              <SelectItem value="delivered">Delivered</SelectItem>
                              <SelectItem value="cancelled">Cancelled</SelectItem>
                            </SelectContent>
                          </Select>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteOrder(order.id, order.orderNumber)}
                            className="text-red-600 border-red-300 hover:bg-red-50 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <div className="space-y-6">
              {/* Live Stats Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-sm text-gray-600">
                    Live data • Updates every 30 seconds
                  </span>
                </div>
                <Button 
                  onClick={fetchAnalytics} 
                  variant="outline" 
                  size="sm"
                  disabled={analyticsLoading}
                >
                  {analyticsLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Refresh'
                  )}
                </Button>
              </div>

              {/* Main Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Live Visitors */}
                <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white">
                  <div className="flex items-center justify-between mb-4">
                    <Activity className="w-8 h-8 opacity-80" />
                    <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                  </div>
                  <p className="text-green-100 text-sm">Live Visitors</p>
                  <p className="text-4xl font-bold">{analytics?.liveVisitors || 0}</p>
                  <p className="text-green-200 text-xs mt-1">Active in last 5 min</p>
                </div>

                {/* Today's Page Views */}
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white">
                  <div className="flex items-center justify-between mb-4">
                    <Eye className="w-8 h-8 opacity-80" />
                  </div>
                  <p className="text-blue-100 text-sm">Today&apos;s Page Views</p>
                  <p className="text-4xl font-bold">{analytics?.today?.pageViews || 0}</p>
                  <p className="text-blue-200 text-xs mt-1">
                    {analytics?.today?.uniqueVisitors || 0} unique visitors
                  </p>
                </div>

                {/* This Week */}
                <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white">
                  <div className="flex items-center justify-between mb-4">
                    <Users className="w-8 h-8 opacity-80" />
                  </div>
                  <p className="text-purple-100 text-sm">This Week</p>
                  <p className="text-4xl font-bold">{analytics?.week?.pageViews || 0}</p>
                  <p className="text-purple-200 text-xs mt-1">
                    {analytics?.week?.uniqueVisitors || 0} unique visitors
                  </p>
                </div>

                {/* This Month */}
                <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-6 text-white">
                  <div className="flex items-center justify-between mb-4">
                    <Globe className="w-8 h-8 opacity-80" />
                  </div>
                  <p className="text-amber-100 text-sm">This Month</p>
                  <p className="text-4xl font-bold">{analytics?.month?.pageViews || 0}</p>
                  <p className="text-amber-200 text-xs mt-1">Total page views</p>
                </div>
              </div>

              {/* Two Column Layout */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Popular Pages */}
                <div className="bg-white rounded-xl shadow-md p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-amber-500" />
                    Popular Pages (Last 7 Days)
                  </h3>
                  <div className="space-y-3">
                    {analytics?.popularPages?.length > 0 ? (
                      analytics.popularPages.map((page, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="w-6 h-6 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center text-xs font-bold">
                              {index + 1}
                            </span>
                            <span className="text-gray-700 font-medium">
                              {page.page === '/' ? 'Home' : page.page.replace('/', '').replace('-', ' ').toUpperCase()}
                            </span>
                          </div>
                          <span className="text-gray-500 font-semibold">{page.views} views</span>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-400 text-center py-4">No data yet</p>
                    )}
                  </div>
                </div>

                {/* Recent Activity */}
                <div className="bg-white rounded-xl shadow-md p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <Activity className="w-5 h-5 text-green-500" />
                    Recent Activity (Live)
                  </h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {analytics?.recentViews?.length > 0 ? (
                      analytics.recentViews.map((view, index) => {
                        const time = new Date(view.timestamp);
                        const timeAgo = Math.floor((Date.now() - time.getTime()) / 1000 / 60);
                        return (
                          <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                              <span className="text-gray-700 text-sm">
                                {view.page === '/' ? 'Home' : view.page}
                              </span>
                            </div>
                            <span className="text-gray-400 text-xs">
                              {timeAgo < 1 ? 'Just now' : timeAgo < 60 ? `${timeAgo}m ago` : `${Math.floor(timeAgo/60)}h ago`}
                            </span>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-gray-400 text-center py-4">No recent activity</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Hourly Chart */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-blue-500" />
                  Today&apos;s Traffic by Hour
                </h3>
                <div className="flex items-end gap-1 h-32">
                  {Array.from({ length: 24 }, (_, hour) => {
                    const hourData = analytics?.hourlyViews?.find(h => h.hour === hour);
                    const views = hourData?.views || 0;
                    const maxViews = Math.max(...(analytics?.hourlyViews?.map(h => h.views) || [1]));
                    const height = views > 0 ? Math.max((views / maxViews) * 100, 10) : 5;
                    const isCurrentHour = new Date().getHours() === hour;
                    
                    return (
                      <div 
                        key={hour} 
                        className="flex-1 flex flex-col items-center gap-1"
                        title={`${hour}:00 - ${views} views`}
                      >
                        <div 
                          className={`w-full rounded-t transition-all ${isCurrentHour ? 'bg-amber-500' : views > 0 ? 'bg-blue-400' : 'bg-gray-200'}`}
                          style={{ height: `${height}%` }}
                        ></div>
                        {hour % 4 === 0 && (
                          <span className="text-xs text-gray-400">{hour}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
                <p className="text-center text-gray-400 text-xs mt-2">Hours (24h format)</p>
              </div>

              {/* Last Updated */}
              {analytics?.lastUpdated && (
                <p className="text-center text-gray-400 text-xs">
                  Last updated: {new Date(analytics.lastUpdated).toLocaleTimeString()}
                </p>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Footer />
    </div>
  );
};

export default Admin;
